import { useEffect, useState } from 'react';
import { apiFetch } from '../hooks/useApi';
import type { ReportInfo, ReportListResponse, ReportResult } from '@shared/api-responses';

function toCsv(result: ReportResult): string {
  const esc = (v: string | number | null) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = result.columns.map((c) => esc(c.label)).join(',');
  const lines = result.rows.map((row) => result.columns.map((c) => esc(row[c.key] ?? null)).join(','));
  return [header, ...lines].join('\r\n');
}

function downloadCsv(result: ReportResult) {
  const blob = new Blob([toCsv(result)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${result.key}-${result.generatedAt.slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Reports() {
  const [reports, setReports] = useState<ReportInfo[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<ReportListResponse>('/reports')
      .then((r) => setReports(r.reports))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load reports'));
  }, []);

  function open(key: string) {
    setActive(key);
    setResult(null);
    setError(null);
    setLoading(true);
    apiFetch<ReportResult>(`/reports/${key}`)
      .then(setResult)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to run report'))
      .finally(() => setLoading(false));
  }

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold text-dcvfd-dark">Reports</h1>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {reports.map((r) => (
          <button
            key={r.key}
            onClick={() => open(r.key)}
            className={`text-left rounded-lg border p-3 transition hover:border-dcvfd-accent ${
              active === r.key ? 'border-dcvfd-accent bg-dcvfd-accent/5' : 'border-border-subtle bg-surface-raised'
            }`}
          >
            <div className="font-semibold text-sm text-dcvfd-dark">{r.title}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{r.description}</div>
          </button>
        ))}
      </div>

      {error && <div className="rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>}
      {loading && <p className="text-sm text-zinc-400 animate-pulse">Running…</p>}

      {result && !loading && (
        <section className="rounded-lg border border-border-subtle bg-surface-raised">
          <div className="flex items-center justify-between gap-3 border-b border-border-subtle px-4 py-2.5 flex-wrap">
            <div>
              <h2 className="text-sm font-semibold text-dcvfd-dark">{result.title}</h2>
              <p className="text-xs text-zinc-500">
                {result.rows.length} {result.rows.length === 1 ? 'row' : 'rows'} · generated{' '}
                {new Date(result.generatedAt).toLocaleString()}
              </p>
            </div>
            <button
              onClick={() => downloadCsv(result)}
              disabled={result.rows.length === 0}
              className="rounded-lg border border-border-default px-3 py-1.5 text-sm text-dcvfd-dark hover:bg-surface disabled:opacity-50"
            >
              Download CSV
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface text-zinc-500 text-left">
                <tr>
                  {result.columns.map((c) => (
                    <th key={c.key} className="px-3 py-2 font-medium whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row, i) => (
                  <tr key={i} className="border-t border-border-subtle">
                    {result.columns.map((c) => (
                      <td key={c.key} className="px-3 py-1.5 whitespace-nowrap">
                        {row[c.key] == null || row[c.key] === '' ? (
                          <span className="text-zinc-300">—</span>
                        ) : (
                          String(row[c.key])
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {result.rows.length === 0 && (
                  <tr>
                    <td colSpan={result.columns.length} className="px-3 py-8 text-center text-zinc-400">
                      No rows.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
